import type { SmartSort } from '@iheartjane/dm-sdk';
import { Flex, Typography } from '@iheartjane/reefer';
import type { LabeledOption, Nullish } from '@iheartjane/types';
import { useMemo } from 'react';

import { MobileFiltersModal } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { FilterSelector } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/filterSelector';
import { ProductGrid } from '@jane/bloom/components/productListingPage/productGrid';
import type { SortOptions } from '@jane/bloom/types/schemas/algolia/product';
import type { Cart } from '@jane/bloom/types/schemas/bloom/checkout';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { BrandSpecials } from '@jane/bloom/types/schemas/marketplace/brandSpecials';
import type {
  ProductApplicableIds,
  StoreSpecials,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type { SpecialsViewModel } from '@jane/bloom/viewModels/specials';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';
import type { AlgoliaProduct } from '@jane/search/types';

import styles from './goldProductGrid.module.css';

export function GoldProductGrid({
  applicablePriceIds,
  brandSpecials,
  cart,
  currentSort,
  customLanguage,
  janeGold,
  pinnedStore,
  preloadedProductIds,
  rowInstance,
  sortOptions,
  storeSpecials,
}: {
  applicablePriceIds?: Nullish<ProductApplicableIds[]>;
  brandSpecials: BrandSpecials;
  cart: Cart;
  currentSort: SortOptions;
  customLanguage: CustomLanguage;
  janeGold: JaneGold;
  pinnedStore: StoreViewModel;
  preloadedProductIds: number[];
  rowInstance: SmartSort<AlgoliaProduct> | undefined;
  sortOptions: LabeledOption<SortOptions>[];
  specialsProducts?: SpecialsViewModel;
  storeSpecials: StoreSpecials;
}) {
  const totalProductCount = useMemo(() => {
    if (!rowInstance) {
      return 0;
    }

    return rowInstance.totalProducts;
  }, [rowInstance]);

  const hasResults = useMemo(() => {
    if (!rowInstance) {
      return false;
    }

    return rowInstance.products.length > 0;
  }, [rowInstance]);

  return (
    <div>
      <FilterSelector
        availableProductsCount={rowInstance?.totalProducts}
        sortOptions={sortOptions}
        currentSort={currentSort}
        shouldShowGold={false}
        hasJaneGoldProducts={true}
        hideLabeledFilters={true}
      />

      <div className={styles['goldMobileFilters']}>
        <Typography variant="body" color="inherit">
          {`${totalProductCount > 999 ? '1k+' : totalProductCount} products`}
        </Typography>
        <MobileFiltersModal
          shouldShowGold={false}
          availableProductsCount={totalProductCount}
          sortOptions={sortOptions}
          currentSort={currentSort}
          hasJaneGoldProducts={true}
        />
      </div>

      {!hasResults && (
        <Flex justifyContent="center" alignItems="center" height="500px">
          <Typography
            color="inherit"
            variant="title-bold"
            data-testid="no-results"
          >
            No Products Available
          </Typography>
        </Flex>
      )}

      <ProductGrid
        brandSpecials={brandSpecials}
        cart={cart}
        customLanguage={customLanguage}
        preloadedProductIds={preloadedProductIds}
        rowInstance={rowInstance as SmartSort<AlgoliaProduct>}
        //TODO: this doesn't seem to apply for product brand specials, but will be necessary for the other brand special types
        // weightFilter={shouldShowWeight({
        //   cartItems: cart?.items || [],
        //   currentSpecial,
        //   specialsProducts,
        // })}
        janeGold={janeGold}
        pinnedStore={pinnedStore}
        applicablePriceIds={applicablePriceIds}
        specials={storeSpecials}
      />
    </div>
  );
}
